import { Stack } from "@mui/material";
import type { FC } from "react";
import { REGISTRATION_STATUS_VARIANTS } from "../../../api/registrations.types";
import type { GridViewRegistration, RegistrationStatus } from "../../../api/registrations.types";
import type { SidePanelGroup } from "../../../dynamicGrid/SidePanels";
import type { Form } from "../../../dynamicForm";
import { DynamicGrid } from "../../../dynamicGrid";
import { StatusSection, StatusSubSidePanel } from "./Status";
import type { RiskTag } from "./RisksFactors";
import { RiskFactorsSection, RiskFactorsSubSidePanel, RISK_TAGS } from "./RisksFactors";

type RegistrationSidePanelProps = {
  activePanelKey: string;
  activeSidePanelGroup: SidePanelGroup;
  data: GridViewRegistration[] | undefined;
  form: Form | undefined;
};

export const RegistrationSidePanel: FC<RegistrationSidePanelProps> = ({
  activePanelKey,
  activeSidePanelGroup,
  data,
  form,
}) => {
  const filteredRowsData = DynamicGrid.useGetFilteredRowsData(data, ({ registrationId }) => registrationId);

  if (activePanelKey === activeSidePanelGroup.mainPanelKey)
    return <OverviewPanel filteredRowsData={filteredRowsData} form={form} />;

  if (activeSidePanelGroup.subPanelKeys.some(subPanelKeys => subPanelKeys === activePanelKey))
    return (
      <OverviewSubSidePanel
        activePanelKey={activePanelKey}
        sidePanelGroup={activeSidePanelGroup}
        filteredRowsData={filteredRowsData}
        form={form}
      />
    );
};

type OverviewPanelProps = { filteredRowsData: GridViewRegistration[]; form: Form | undefined };
const OverviewPanel: FC<OverviewPanelProps> = ({ filteredRowsData, form }) => {
  if (!form) return null;

  return (
    <Stack sx={{ gap: 6 }}>
      <StatusSection filteredRowsData={filteredRowsData} />
      <RiskFactorsSection filteredRowsData={filteredRowsData} form={form} />
    </Stack>
  );
};

type OverviewSubSidePanelProps = {
  activePanelKey: string;
  sidePanelGroup: SidePanelGroup;
  filteredRowsData: GridViewRegistration[];
  form: Form | undefined;
};
const OverviewSubSidePanel: FC<OverviewSubSidePanelProps> = ({
  activePanelKey,
  sidePanelGroup,
  filteredRowsData,
  form,
}) => {
  if (REGISTRATION_STATUS_VARIANTS.some(status => status === activePanelKey)) {
    return (
      <StatusSubSidePanel
        status={activePanelKey as RegistrationStatus["status"]}
        sidePanelGroup={sidePanelGroup}
        filteredRowsData={filteredRowsData}
      />
    );
  }

  if (RISK_TAGS.some(tag => tag === activePanelKey)) {
    if (!form) return null;

    return (
      <RiskFactorsSubSidePanel
        form={form}
        riskTag={activePanelKey as RiskTag}
        sidePanelGroup={sidePanelGroup}
        filteredRowsData={filteredRowsData}
      />
    );
  }
};
